import React, {FC, useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {CertificateViewer} from './CertificateViewer'
import {useAuth} from '../../auth'
import type {
  SafetyCommitteeClosureResult,
  SafetyCommitteeStatus,
} from '../core/_models'
import {
  safetyCommitteeAttachmentDownloadUrl,
  safetyCommitteeAttachmentViewUrl,
} from '../core/_requests'

type AttachmentItem = {name: string}

type SCMStatus = SafetyCommitteeStatus

type SCMRecord = {
  id: number
  dateHeld: string
  findingName: string
  findingsLinkType?: 'AUDIT' | 'INSPECTION' | 'MONTHLY' | '' | null
  officeRemarks: string
  officeAttachments: AttachmentItem[]
  status: SCMStatus
  officeReviewed?: boolean
  closureResult?: SafetyCommitteeClosureResult | null
}

const getStatusDisplay = (
  record: {
    status?: SCMStatus | null
    officeReviewed?: boolean
    closureResult?: SafetyCommitteeClosureResult | null
  },
  shipboardUser?: boolean
) => {
  const status = record?.status
  if (!status) {
    return {label: '-', badgeClass: 'badge-light'}
  }
  if (status === 'SAVE') {
    return {label: 'Saved', badgeClass: 'badge-light-secondary'}
  }
  if (status === 'SUBMIT') {
    if (shipboardUser) {
      return {label: 'Submitted', badgeClass: 'badge-light-warning'}
    }
    if (record?.officeReviewed) {
      return {label: 'Reviewed', badgeClass: 'badge-light-success'}
    }
    return {label: 'Ready for Review', badgeClass: 'badge-light-warning'}
  }
  if (status === 'CLOSE') {
    if (record?.closureResult === 'TO_BE_REVIEWED_NEXT_INSPECTION') {
      return {label: 'Closed and TBR', badgeClass: 'badge-light-success'}
    }
    return {label: 'Closed Satisfactorily', badgeClass: 'badge-light-success'}
  }
  return {label: String(status), badgeClass: 'badge-light'}
}

const attachmentsTooltip = (items: AttachmentItem[] | undefined | null) => {
  if (!items || items.length === 0) return ''
  return items.map(i => i.name).join(', ')
}

export type SCMReviewModalProps = {
  visible: boolean
  record: SCMRecord | null
  shipboardUser: boolean
  onClose: () => void
  onSave: (officeRemarks: string, officeAttachments: File[]) => void
  onCloseWithResult: (
    officeRemarks: string,
    officeAttachments: File[],
    result: SafetyCommitteeClosureResult
  ) => void
}

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'

export const SCMReviewModal: FC<SCMReviewModalProps> = ({
  visible,
  record,
  shipboardUser,
  onClose,
  onSave,
  onCloseWithResult,
}) => {
  const {auth} = useAuth()
  const token = auth?.auth.jwt
  const [officeRemarks, setOfficeRemarks] = useState('')
  const [officeAttachments, setOfficeAttachments] = useState<File[]>([])
  const [remarksError, setRemarksError] = useState('')
  const [viewerFile, setViewerFile] = useState<{
    name: string
    viewUrl: string
    downloadUrl?: string
  } | null>(null)

  useEffect(() => {
    if (!visible || !record) return
    setOfficeRemarks(record.officeRemarks || '')
    setOfficeAttachments([])
    setRemarksError('')
  }, [visible, record])

  const validateRemarks = () => {
    if (!officeRemarks || !officeRemarks.trim()) {
      setRemarksError('Office remarks are required.')
      return false
    }
    setRemarksError('')
    return true
  }

  const handleDownload = async (downloadUrl?: string, fallbackName?: string) => {
    try {
      if (!downloadUrl) throw new Error('No download URL available')
      const res = await fetch(downloadUrl, {
        headers: token ? {Authorization: `Bearer ${token}`} : undefined,
      })
      if (!res.ok) throw new Error(`Download failed: ${res.status}`)
      const cd = res.headers.get('content-disposition')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = (() => {
        const m1 = /filename\*\s*=\s*[^']*'[^']*'([^;]+)/i.exec(cd || '')
        if (m1?.[1]) return decodeURIComponent(m1[1])
        const m2 = /filename\s*=\s*"([^"]+)"/i.exec(cd || '')
        if (m2?.[1]) return m2[1]
        const m3 = /filename\s*=\s*([^;]+)/i.exec(cd || '')
        if (m3?.[1]) return m3[1]
        return fallbackName || 'document.pdf'
      })()
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err: any) {
      console.error('Download error:', err)
      toast.error('Download failed')
    }
  }

  if (!visible || !record) return null
  const existingOfficeAttachments = record.officeAttachments || []
  const newOfficeItems = officeAttachments.map(f => ({name: f.name}))

  const WRAP_STYLE: React.CSSProperties = {
    background: 'rgba(0,0,0,0.5)',
    position: 'fixed',
    inset: 0,
    zIndex: 1050,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  return (
    <>
      <div style={WRAP_STYLE} tabIndex={-1}>
        <div
          className='bg-white text-dark rounded shadow-lg d-flex flex-column'
          style={{
            width: '70vw',
            maxWidth: '900px',
            maxHeight: '80vh',
          }}
        >
          {/* Header */}
          <div className='d-flex align-items-center justify-content-between border-bottom px-5 py-4 flex-shrink-0'>
            <div>
              <h5 className='modal-title text-dark m-0'>Review / Close SCM</h5>
              <div className='text-muted small mt-1'>
                Meeting Date: {record.dateHeld || '-'}
                {record.findingName ? ` • Finding: ${record.findingName}` : ''}
                {record.findingsLinkType ? (
                  <span
                    className={`badge ms-2 ${
                      record.findingsLinkType === 'AUDIT'
                        ? 'badge-light-info'
                        : record.findingsLinkType === 'INSPECTION'
                          ? 'badge-light-primary'
                          : 'badge-light-warning'
                    }`}
                  >
                    {record.findingsLinkType === 'AUDIT'
                      ? 'Audit'
                      : record.findingsLinkType === 'INSPECTION'
                        ? 'Incident'
                        : 'Monthly'}
                  </span>
                ) : null}
              </div>
            </div>
            <div className='d-flex align-items-center gap-3'>
              {(() => {
                const statusMeta = getStatusDisplay(record, shipboardUser)
                return (
                  <span className={`badge ${statusMeta.badgeClass}`}>
                    {statusMeta.label}
                  </span>
                )
              })()}
              <button type='button' className='btn-close m-0' onClick={onClose} />
            </div>
          </div>

          {/* Body with inner scroll */}
          <div className='flex-grow-1 overflow-auto px-5 py-4'>
            <div className='mb-4'>
              <label className={LABEL}>
                Office Remarks <span style={{color: '#dc3545'}}>*</span>
              </label>
              <textarea
                className='form-control'
                rows={4}
                value={officeRemarks}
                onChange={e => {
                  setOfficeRemarks(e.target.value)
                  if (remarksError) setRemarksError('')
                }}
                placeholder='Summarise review remarks'
              />
              {remarksError && (
                <div className='text-danger small mt-2'>{remarksError}</div>
              )}
            </div>

            <div className='mb-3'>
              <label className={LABEL}>Additional Supporting Documents</label>
              <input
                type='file'
                className='form-control'
                multiple
                onChange={e =>
                  setOfficeAttachments(e.target.files ? Array.from(e.target.files) : [])
                }
              />
              <div className='text-muted small mt-2'>
                Existing attachments (if any) will remain. New uploads will be added.
              </div>

              {officeAttachments.length > 0 && (
                <div
                  className='form-text'
                  style={{
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    maxWidth: '100%',
                  }}
                  title={attachmentsTooltip(newOfficeItems)}
                >
                  Selected: {attachmentsTooltip(newOfficeItems)}
                </div>
              )}

              {existingOfficeAttachments.length > 0 && (
                <div className='mt-3'>
                  <label className={LABEL}>Existing Office Attachments</label>
                  <ul className='list-unstyled mb-0'>
                    {existingOfficeAttachments.map((f, idx) => {
                      const viewUrl = safetyCommitteeAttachmentViewUrl(record.id, 'OFFICE', f.name)
                      const downloadUrl = safetyCommitteeAttachmentDownloadUrl(record.id, 'OFFICE', f.name)
                      return (
                        <li key={`${f.name}-${idx}`} className='d-flex align-items-center gap-2 mb-2'>
                          <span className='text-dark'>{f.name}</span>
                          <button
                            type='button'
                            className='btn btn-light-primary btn-sm'
                            onClick={() =>
                              setViewerFile({
                                name: f.name,
                                viewUrl,
                                downloadUrl,
                              })
                            }
                          >
                            View
                          </button>
                          <button
                            type='button'
                            className='btn btn-light btn-sm'
                            onClick={() => {
                              handleDownload(downloadUrl, f.name)
                            }}
                          >
                            Download
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className='border-top d-flex justify-content-end gap-2 px-5 py-4 flex-shrink-0'>
            <button type='button' className='btn btn-light btn-sm' onClick={onClose}>
              Cancel
            </button>
            <button
              type='button'
              className='btn btn-light-primary btn-sm'
              onClick={() => {
                if (!validateRemarks()) return
                onSave(officeRemarks, officeAttachments)
              }}
            >
              Save Review Only
            </button>
            <button
              type='button'
              className='btn btn-success btn-sm'
              onClick={() => {
                if (!validateRemarks()) return
                onCloseWithResult(officeRemarks, officeAttachments, 'SATISFACTORY')
              }}
            >
              Close – Satisfactory
            </button>
            <button
              type='button'
              className='btn btn-warning btn-sm text-white'
              onClick={() => {
                if (!validateRemarks()) return
                onCloseWithResult(officeRemarks, officeAttachments, 'TO_BE_REVIEWED_NEXT_INSPECTION')
              }}
            >
              Close – To Be Reviewed
            </button>
          </div>
        </div>
      </div>
      {viewerFile && (
        <CertificateViewer
          file={viewerFile || undefined}
          onClose={() => setViewerFile(null)}
        />
      )}
    </>
  )
}
